/**
 * Operator node-report chat inject prompts (tagged user messages → agent reply).
 */

import type { DrillAncestor } from "./pfm-drill-keys.ts";
import {
  nodeReportLanguageProfile,
  resolveNodeReportLanguage,
  type NodeReportLanguage,
} from "./node-report-language.ts";
import type { UiLocale } from "./i18n.ts";
import { PFM_NODE_REPORT_EXPLORATION_NEEDED_MSG } from "./node-report-standards.ts";

export type PfmNodePromptContext = {
  executionId: string;
  templateId: string;
  runName: string;
  targetUrl: string;
  /** Authoritative mindmap key — agent MUST save under this key. */
  nodeKey: string;
  nodeTitle: string;
  parentNodeKey: string;
  level: number;
  parentPath: string;
  /** 1-based display index for ### Node N: title */
  nodeIndex: number;
};

export type NodeReportRequestPromptOptions = {
  reqId: string;
  ctx: PfmNodePromptContext;
  refresh?: boolean;
  baselineMarkdown?: string;
  baselineSourceLabel?: string;
  detailed?: boolean;
  /** Run-level AI answer language (from execution settings). */
  aiAnswerLanguage?: string;
  /** UI locale fallback when run language is unset. */
  locale?: UiLocale;
  /** Existing report timestamp (ms) to compare newer run evidence/activity. */
  baselineGeneratedAtMs?: number;
  /** Whether the existing baseline already satisfies mandatory long format. */
  baselineIsFull?: boolean;
};

export type NodeReportTranslatePromptOptions = {
  reqId: string;
  sourceMarkdown: string;
  targetLang: NodeReportLanguage;
};

/** Required agent output shape (markdown headings). */
export function buildNodeReportOutputTemplate(
  ctx: PfmNodePromptContext,
  language?: NodeReportLanguage | string,
): string {
  const lang = resolveNodeReportLanguage({ aiAnswerLanguage: language });
  const p = nodeReportLanguageProfile(lang);
  const parentKey = String(ctx.parentNodeKey || "").trim() || "null";
  const nodeKeyLabel =
    lang === "en" ? "Node Key" : lang === "zh" ? "节点键" : lang === "ja" ? "ノードキー" : "노드 키";
  const parentKeyLabel =
    lang === "en"
      ? "Parent Node Key"
      : lang === "zh"
        ? "父节点键"
        : lang === "ja"
          ? "親ノードキー"
          : "상위 노드 키";
  const levelLabel =
    lang === "en" ? "Level" : lang === "zh" ? "层级" : lang === "ja" ? "レベル" : "레벨";
  return [
    `### ${p.nodeLabel} ${ctx.nodeIndex}: ${ctx.nodeTitle}`,
    `- **${nodeKeyLabel}:** \`${ctx.nodeKey}\``,
    `- **${parentKeyLabel}:** \`${parentKey}\``,
    `- **${levelLabel}:** \`${ctx.level}\``,
    "",
    `#### ${p.headings.nodeDescription}`,
    "<Detailed scope and business/architectural purpose using consistent entity nomenclature>",
    "",
    `#### ${p.headings.mainFeatures}`,
    `##### ${p.headings.feature} 1: <Feature Title>`,
    `**${p.headings.feature} Description:** <Detailed feature description>`,
    "",
    `###### ${p.headings.testCase} 1.1: <Test Case Title>`,
    `**${p.fields.objective}:** <What this test verifies>`,
    `**${p.fields.preconditions}:** <Setup requirements or Not observed yet>`,
    `**${p.fields.testData}:** <Inputs used or Not observed yet>`,
    `**${p.headings.procedureSteps}:**`,
    "1. <Action Step 1>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 1>`,
    "2. <Action Step 2>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 2>`,
    "3. <Action Step 3>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 3>`,
    "",
    `###### ${p.headings.testCase} 1.2: <Test Case Title>`,
    `**${p.fields.objective}:** <...>`,
    `**${p.fields.preconditions}:** <...>`,
    `**${p.fields.testData}:** <...>`,
    `**${p.headings.procedureSteps}:**`,
    "1. <Action Step 1>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 1>`,
    "2. <Action Step 2>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 2>`,
    "3. <Action Step 3>  ",
    `   **${p.fields.expectedResult}:** <Expected Result 3>`,
    "",
    `##### ${p.headings.feature} 2: <Feature Title>`,
    `(repeat ${p.headings.feature} / ${p.headings.testCase} / ${p.headings.procedureSteps} structure)`,
  ].join("\n");
}

/** When features, test cases, and steps are not verified yet for this node. */
export function buildNodeReportIncompleteTemplate(
  ctx: PfmNodePromptContext,
  language?: NodeReportLanguage | string,
): string {
  const lang = resolveNodeReportLanguage({ aiAnswerLanguage: language });
  const p = nodeReportLanguageProfile(lang);
  const parentKey = String(ctx.parentNodeKey || "").trim() || "null";
  const explorationHeading =
    lang === "en"
      ? "Exploration Status"
      : lang === "zh"
        ? "探索状态"
        : lang === "ja"
          ? "探索状況"
          : "탐색 상태";
  const nodeKeyLabel =
    lang === "en" ? "Node Key" : lang === "zh" ? "节点键" : lang === "ja" ? "ノードキー" : "노드 키";
  const parentKeyLabel =
    lang === "en"
      ? "Parent Node Key"
      : lang === "zh"
        ? "父节点键"
        : lang === "ja"
          ? "親ノードキー"
          : "상위 노드 키";
  const levelLabel =
    lang === "en" ? "Level" : lang === "zh" ? "层级" : lang === "ja" ? "レベル" : "레벨";
  return [
    `### ${p.nodeLabel} ${ctx.nodeIndex}: ${ctx.nodeTitle}`,
    `- **${nodeKeyLabel}:** \`${ctx.nodeKey}\``,
    `- **${parentKeyLabel}:** \`${parentKey}\``,
    `- **${levelLabel}:** \`${ctx.level}\``,
    "",
    `#### ${p.headings.nodeDescription}`,
    "<What is known about this node's scope and purpose from this run's evidence>",
    "",
    `#### ${explorationHeading}`,
    PFM_NODE_REPORT_EXPLORATION_NEEDED_MSG,
    "",
    `(Do not add #### ${p.headings.mainFeatures} until you have verified features, test cases, and procedure steps.)`,
  ].join("\n");
}

export function buildPfmNodePromptContext(input: {
  executionId: string;
  templateId?: string;
  runName?: string;
  targetUrl?: string;
  nodeKey: string;
  nodeTitle: string;
  parents?: DrillAncestor[];
}): PfmNodePromptContext {
  const parents = input.parents || [];
  const lastParent = parents[parents.length - 1];
  const parentPath = ["Top", ...parents.map((p) => p.title), input.nodeTitle]
    .filter((s) => String(s || "").trim())
    .join(" > ");
  const level = Math.max(0, parents.length);
  return {
    executionId: String(input.executionId || "").trim(),
    templateId: String(input.templateId || "").trim(),
    runName: String(input.runName || "").trim(),
    targetUrl: String(input.targetUrl || "").trim(),
    nodeKey: String(input.nodeKey || "").trim(),
    nodeTitle: String(input.nodeTitle || input.nodeKey || "").trim(),
    parentNodeKey: String(lastParent?.nodeKey || "").trim(),
    level,
    parentPath,
    nodeIndex: level + 1,
  };
}

/** Tagged chat inject for generate or refresh (shown in operator chat transcript). */
export function buildNodeReportRequestPrompt(options: NodeReportRequestPromptOptions): string {
  const { reqId, ctx, refresh = false, baselineMarkdown } = options;
  const baseline = String(baselineMarkdown || "").trim();
  const baselinePromptCap = 6000;
  const baselineSnippet =
    baseline.length > baselinePromptCap
      ? `${baseline.slice(0, baselinePromptCap)}\n…(baseline truncated for prompt size; keep structure and improve where needed)`
      : baseline;
  const baselineLabel = String(options.baselineSourceLabel || "prior saved report").trim();
  const baselineSavedAt =
    typeof options.baselineGeneratedAtMs === "number" && options.baselineGeneratedAtMs > 0
      ? new Date(options.baselineGeneratedAtMs).toISOString()
      : "";
  const baselineIsFull = options.baselineIsFull !== false;
  const lang: NodeReportLanguage = "en";
  const langProfile = nodeReportLanguageProfile(lang);
  const outputTemplate = buildNodeReportOutputTemplate(ctx, lang);
  const incompleteTemplate = buildNodeReportIncompleteTemplate(ctx, lang);

  const lines = [
    `[Node-Report-Request-Id: ${reqId}]`,
    refresh ? "[Node-Report-Refresh]" : "[Node-Report-Generate]",
    "",
    refresh
      ? "Refresh the PFM node EAD report for the selected node using this run's evidence and the baseline below when provided."
      : "Generate a detailed PFM node EAD report ONLY for the selected PFM node.",
    "",
    `Run: ${ctx.runName || "—"}`,
    `Execution: ${ctx.executionId}`,
    `Target: ${ctx.targetUrl || "—"}`,
    `Parent path: ${ctx.parentPath}`,
    "",
    "Language:",
    `- Write the **entire** report (all headings, field labels, and body text) in **${langProfile.name}**.`,
    "- Keep the same markdown structure, heading levels, and section order as the template below.",
  ];

  lines.push(
    "",
    "Hard scope rules:",
    "1) Content only for this node_key — no child or grandchild nodes.",
    "2) Use only evidence from this run (screenshots, activity log, exploration).",
    baselineSavedAt
      ? `3) Treat the baseline report as the current canonical node report. Improve content only when you found newer run evidence/activity after ${baselineSavedAt}.`
      : "3) Treat the baseline report as the current canonical node report. Improve content only when you found newer run evidence/activity.",
    "4) If no newer evidence/activity exists, return `no_new_findings` and keep prior report content.",
    "5) Save via commit_pfm_snapshot under the exact node_key above when delivery applies.",
    "",
    "Report composition (required):",
    "1) Node title — in the ### Node heading.",
    "2) Node description — #### Node Description.",
    "3) Related features — #### Main Features with ##### Feature sections.",
    "4) Test cases — ###### Test Case under each feature.",
    "5) Test steps — numbered Procedure Steps with Expected Result per step.",
    "",
    "If you have NOT verified features, test cases, and procedure steps for this node:",
    "- Do NOT invent Features, Test Cases, or Steps.",
    "- Use the incomplete template below (Node Description + Exploration Status only).",
    `- The Exploration Status line must be exactly: ${PFM_NODE_REPORT_EXPLORATION_NEEDED_MSG}`,
    "",
    "Reply protocol:",
    `- Line 1 must be exactly: [Node-Report-Reply-To: ${reqId}]`,
    baselineIsFull
      ? "- If nothing meaningful changed vs baseline: `no_new_findings: <one line reason>`."
      : "- Baseline is not a full report. Do NOT return `no_new_findings`; you MUST output a complete full report.",
    "- Else: output either the **full** report (when verified) or the **incomplete** report (when not).",
    `- Full report requires: ${langProfile.headings.feature}, ${langProfile.headings.testCase}, and numbered ${langProfile.headings.procedureSteps}.`,
  );

  lines.push(
    "",
    "Full report template (when features + test cases + steps are verified):",
    outputTemplate,
    "",
    "Incomplete report template (no verified features / test cases / steps yet):",
    incompleteTemplate,
  );

  if (refresh && baselineSnippet) {
    lines.push(
      "",
      `Baseline (${baselineLabel}) — preserve full length; do not shorten:`,
      "----- BEGIN BASELINE REPORT -----",
      baselineSnippet,
      "----- END BASELINE REPORT -----",
    );
  } else if (refresh && !baselineSnippet) {
    lines.push("", "No baseline markdown was loaded — treat as first full report for this node on this run.");
  }

  lines.push(
    "",
    "Quality bar (required — do not produce a short summary instead):",
    `- At least 2 ${langProfile.headings.feature} sections; each with at least 2 ${langProfile.headings.testCase} entries.`,
    `- Each test case must include at least 3 numbered procedure steps, each with a **${langProfile.fields.expectedResult}:** line.`,
    "- Include full Node Description prose (not one sentence).",
    "- Do not use markdown tables.",
    "- Do not omit Features/Test Cases/Steps to save space.",
    "- Tie claims to this run's screenshots or artifacts when possible.",
  );

  if (options.detailed) {
    lines.push("- Include edge cases and error paths where observed.");
  }

  return lines.join("\n");
}

export function buildNodeReportTranslatePrompt(options: NodeReportTranslatePromptOptions): string {
  const reqId = String(options.reqId || "").trim();
  const target = nodeReportLanguageProfile(options.targetLang);
  const source = String(options.sourceMarkdown || "").trim();
  const promptCap = 6000;
  const sourceSnippet = source.length > promptCap ? `${source.slice(0, promptCap)}\n...(truncated)` : source;
  return [
    `[Node-Report-Translate-Request-Id: ${reqId}]`,
    "Translate the markdown report below.",
    "",
    "Rules:",
    `- Reply line 1 exactly: [Node-Report-Translate-Reply-To: ${reqId}]`,
    `- Translate the entire report to ${target.name}.`,
    `- Keep ALL long-format sections intact: ${target.headings.mainFeatures}, ${target.headings.feature}, ${target.headings.testCase}, and numbered ${target.headings.procedureSteps}.`,
    "- Preserve heading levels, numbering, bullets, and section order.",
    "- Do not summarize, omit sections, or rewrite structure.",
    "- Return markdown only after the reply marker line.",
    "",
    "Source report:",
    "----- BEGIN ENGLISH NODE REPORT -----",
    sourceSnippet,
    "----- END ENGLISH NODE REPORT -----",
  ].join("\n");
}

/** @deprecated Use buildNodeReportRequestPrompt — kept for imports. */
export function buildNodeReportRefreshPrompt(
  options: Omit<NodeReportRequestPromptOptions, "refresh">,
): string {
  return buildNodeReportRequestPrompt({ ...options, refresh: true });
}

export function buildNodeReportWakePrompt(
  reqId: string,
  options?: { locale?: UiLocale; aiAnswerLanguage?: string },
): string {
  const lang = nodeReportLanguageProfile(
    resolveNodeReportLanguage({
      aiAnswerLanguage: options?.aiAnswerLanguage,
      locale: options?.locale,
    }),
  );
  return [
    `Please process the immediately previous request [Node-Report-Request-Id: ${reqId}].`,
    `Begin your answer with this exact line: [Node-Report-Reply-To: ${reqId}]`,
    `Follow the required report structure from that request (node title + description; add ${lang.headings.mainFeatures} / ${lang.headings.testCase} / steps when verified, otherwise use Exploration Status with the exploration-needed message — not a short summary).`,
  ].join("\n");
}
